import styled, { css } from 'styled-components'

import { ButtonUnstyled } from '@sketch/components'

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex[9]};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 16px 16px;
`

export const Dialog = styled.div`
  ${({ theme }) => css`
    width: min(540px, 100%);
    max-height: min(680px, calc(100vh - 128px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: ${theme.radii.xxxlarge};
    background: ${theme.colors.background.secondary.A};
    box-shadow:
      0 18px 48px rgba(0, 0, 0, 0.24),
      0 0 0 1px ${theme.colors.border.A};
  `}
`

export const SearchRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid ${theme.colors.border.A};
  `}
`

export const SearchIconWrapper = styled.div`
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.foreground.secondary.C};

  svg {
    width: 18px;
    height: 18px;
  }
`

export const Input = styled.input`
  ${({ theme }) => css`
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${theme.colors.foreground.secondary.A};
    font-size: ${theme.fontSizes.E};
    line-height: 24px;
    letter-spacing: 0;

    &::placeholder {
      color: ${theme.colors.foreground.secondary.D};
    }
  `}
`

export const Results = styled.div`
  overflow-y: auto;
  padding: 8px;
`

export const Section = styled.div`
  & + & {
    margin-top: 8px;
  }
`

export const SectionTitle = styled.div`
  ${({ theme }) => css`
    padding: 8px 8px 4px;
    color: ${theme.colors.foreground.secondary.D};
    font-size: ${theme.fontSizes.A};
    font-weight: ${theme.fontWeights.semibold};
    line-height: 14px;
    letter-spacing: 0;
    text-transform: uppercase;
  `}
`

export const ResultIcon = styled.div`
  background: ${({ theme }) =>
    theme._name === 'dark'
      ? theme.colors.background.tertiary.C
      : theme.colors.background.tertiary.C};
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  border-radius: ${({ theme }) => theme.radii.xlarge};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;
  }
`

export const PreviewIconFrame = styled.div`
  ${({ theme }) => css`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid ${theme.colors.border.A};
    border-radius: 6px;
    background: ${theme.colors.background.secondary.B};
  `}
`

export const PreviewIconImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

export const ResultText = styled.div`
  min-width: 0;
`

export const ResultTitle = styled.div`
  ${({ theme }) => css`
    overflow: hidden;
    font-size: ${theme.fontSizes.E};
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;

    mark {
      background: ${theme.colors.brand.yellow.default.D};
      box-shadow: inset 0 -1px 0 0 ${theme.colors.brand.yellow.default.A};
      color: inherit;
    }
  `}
`

export const ResultSubtitle = styled.div`
  ${({ theme }) => css`
    overflow: hidden;
    color: ${theme.colors.foreground.secondary.C};
    font-size: ${theme.fontSizes.C};
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;

    mark {
      background: ${theme.colors.brand.yellow.default.D};
      box-shadow: inset 0 -1px 0 0 ${theme.colors.brand.yellow.default.A};
      color: inherit;
    }
  `}
`

export const Shortcut = styled.kbd`
  ${({ theme }) => css`
    min-width: 24px;
    padding: 3px 6px;
    border-radius: 5px;
    background: ${theme.colors.background.secondary.B};
    color: ${theme.colors.foreground.secondary.C};
    font-family: inherit;
    font-size: ${({ theme }) => theme.fontSizes.C};
    line-height: 14px;
    text-align: center;
  `}
`

export const ResultItem = styled(ButtonUnstyled)<{ $active: boolean }>(
  ({ theme, $active }) => css`
    width: 100%;
    min-height: 44px;
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: ${theme.radii.xxlarge};
    color: ${theme.colors.foreground.secondary.A};
    text-align: left;

    ${$active &&
    css`
      color: ${theme._name === 'dark'
        ? theme.colors.foreground.secondary.A
        : theme.colors.foreground.primary.A};
      background: ${theme._name === 'dark'
        ? theme.colors.sketch.B
        : theme.colors.sketch.A};

      ${ResultSubtitle} {
        color: ${theme._name === 'dark'
          ? theme.colors.foreground.secondary.B
          : theme.colors.foreground.primary.B};
      }

      ${Shortcut} {
        color: ${theme._name === 'dark'
          ? theme.colors.foreground.secondary.A
          : theme.colors.foreground.primary.A};
        background: none;
      }

      ${ResultIcon} {
        background: hsla(0, 0%, 100%, 0.16);
        color: ${theme._name === 'dark'
          ? theme.colors.foreground.secondary.A
          : theme.colors.foreground.primary.A};
      }
    `}

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `
)

export const StateMessage = styled.div`
  ${({ theme }) => css`
    padding: 24px 12px;
    color: ${theme.colors.foreground.secondary.C};
    font-size: ${({ theme }) => theme.fontSizes.D};
    line-height: 18px;
    text-align: center;
  `}
`
